import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { afmelden, laadAangemeldeGebruiker } from '../api/auth.ts';
import { bijVerlopenSessie, zetCsrfToken } from '../api/client.ts';
import type { AangemeldeGebruiker } from '../api/typen.ts';

export interface Sessie {
  /** De aangemelde gebruiker, of `null` als niemand is aangemeld. */
  ik: AangemeldeGebruiker | null;
  /** Of de aanmeldstatus nog wordt opgehaald. */
  laden: boolean;
  /** De fout als de aanmeldstatus niet op te halen is, bijvoorbeeld omdat de API niet bereikbaar is. */
  fout: unknown;
  /** Of de aangemelde gebruiker beheerder is. */
  isBeheerder: boolean;
  /** Haalt de aanmeldstatus opnieuw op. */
  herlaad(): Promise<void>;
  /** Vervangt de aangemelde gebruiker, bijvoorbeeld na het verversen van de sessie. */
  vervang(ik: AangemeldeGebruiker): void;
  /** Meldt af, en met `overal` ook op andere apparaten. */
  meldAf(overal?: boolean): Promise<void>;
}

const SessieContext = createContext<Sessie | null>(null);

/**
 * Houdt bij wie is aangemeld. Bij het starten wordt `/auth/me` opgehaald; het CSRF-token daaruit gaat naar de
 * client, zodat wijzigende verzoeken het meesturen. Meldt de API dat de sessie verlopen is, dan is de gebruiker
 * meteen afgemeld in de app.
 * @param props De inhoud van de app.
 * @returns De provider.
 */
export function SessieProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [ik, setIk] = useState<AangemeldeGebruiker | null>(null);
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState<unknown>(null);

  const vervang = useCallback((nieuw: AangemeldeGebruiker | null) => {
    setIk(nieuw);
    zetCsrfToken(nieuw?.csrfToken ?? null);
  }, []);

  const herlaad = useCallback(async () => {
    try {
      vervang(await laadAangemeldeGebruiker());
      setFout(null);
    } catch (gevangen) {
      setFout(gevangen);
    } finally {
      setLaden(false);
    }
  }, [vervang]);

  useEffect(() => {
    void herlaad();
  }, [herlaad]);

  useEffect(() => {
    bijVerlopenSessie(() => {
      vervang(null);
      queryClient.clear();
    });
    return () => bijVerlopenSessie(null);
  }, [queryClient, vervang]);

  const meldAf = useCallback(
    async (overal = false) => {
      try {
        await afmelden(overal);
      } finally {
        vervang(null);
        queryClient.clear();
      }
    },
    [queryClient, vervang],
  );

  const waarde = useMemo<Sessie>(
    () => ({ ik, laden, fout, isBeheerder: ik?.gebruiker.rol === 'beheerder', herlaad, vervang, meldAf }),
    [ik, laden, fout, herlaad, vervang, meldAf],
  );

  return <SessieContext.Provider value={waarde}>{children}</SessieContext.Provider>;
}

/**
 * Geeft de sessie.
 * @returns De sessie.
 */
export function useSessie(): Sessie {
  const sessie = useContext(SessieContext);
  if (!sessie) {
    throw new Error('useSessie werkt alleen binnen SessieProvider.');
  }
  return sessie;
}

/**
 * Geeft de aangemelde gebruiker, voor onderdelen die alleen aangemeld bereikbaar zijn.
 * @returns De aangemelde gebruiker.
 */
export function useIk(): AangemeldeGebruiker {
  const { ik } = useSessie();
  if (!ik) {
    throw new Error('Er is niemand aangemeld.');
  }
  return ik;
}
